import React, { useEffect, useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Paper from '@mui/material/Paper';

import { ErrorCode } from '../../../../ErrorCode';

import OKCancelDialog from '../../../atoms/Dialog/OKCancelDialog';
import SimpleDialog from '../../../atoms/Dialog/SimpleDialog';

import { passwordCheckType } from './LoginUserSetting';
import { TableBaseStyle, LoginUserSettingStyle } from './LoginUserSettingStyle';

import LoginUserSettingPassWordChangeBody from './LoginUserSettingPassWordChangeBody';
import { USER_MNG, GetUserInfos, UpdatePassword, CheckPasswordFormat } from '../../../../function/UserManageModel';
import { AppContext } from '../../../../App';

type LoginUserSettingGridProps = {
  tableContainerStyle: string;
  updateCells: (cells: USER_MNG[]) => void;
  cellRefresh: string;
  removeIndexes: number[];
};

const LoginUserSettingGrid: React.FC<LoginUserSettingGridProps> = (props) => {
  const { t } = useTranslation();
  const tableBase = TableBaseStyle();

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [messageDialogOpen, setMessageDialogOpen] = React.useState(false);
  const [showMessage, setShowMessage] = React.useState<string>('');

  const [passCheck, setPassCheck] = React.useState<passwordCheckType>({
    indexNo: 0,
    nowPassword: '',
    newPassword: '',
    confirmationPassword: '',
    hasPassword: false,
  });

  const [cells, setCells] = useState<USER_MNG[]>([]);

  const { login } = useContext(AppContext);
  //ワンタイムパスワード有効フラグ
  const addressUseenable = login.GetSystemSettingModel.OnetimePasswordEnable === 1 ? true : false;
  /**
   * 表示更新
   */
  useEffect(() => {
    GetUserInfos().then((result: USER_MNG[]) => {
      //console.debug(['GetUserInfos result', result]);
      result.forEach((um) => {
        um.hasPassword = um.USER_ID !== '' && um.PASSWORD !== '';
        um.PASSWORD = '';
      });
      setCells([...result]);
      props.updateCells([...result]);
    });
  }, [props.cellRefresh]);

  useEffect(() => {
    props.removeIndexes.forEach((rmv) => {
      cells[rmv].USER_ID = '';
      cells[rmv].PASSWORD = '';
      cells[rmv].SET_AUTH = 0;
      cells[rmv].DEF = 0;
      cells[rmv].MAIL_ADDRESS = '';
    });
    setCells([...cells]);
    props.updateCells([...cells]);
  }, [props.removeIndexes]);

  /**
   * ダイアログで変更した値を管理
   * @param key
   * @param value
   */
  const updatecheckPassword = (key: string, value: string): void => {
    var _passwordCheck = { ...passCheck };
    _passwordCheck = { ..._passwordCheck, [key]: value };
    setPassCheck(_passwordCheck);
  };

  /**
   * グリッド上で値を変更した内容を管理
   * @param index
   * @param key
   * @returns
   */
  const onChangeCell = (index: number, key: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    //console.debug(['onChangeCell()', index, key]);
    const _cells = [...cells];
    _cells[index] = { ..._cells[index], [key]: event.target.value };
    _cells[index].isEdit = true;
    setCells(_cells);
    props.updateCells(_cells);
  };

  /**
   * グリッド上のチェック変更の管理
   */
  const onChangeSwitchCell = (index: number, key: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const _cells = [...cells];

    //console.debug(['onChangeSwitchCell()', index, key, " event.target.checke:", event.target.checked, _cells]);
    //デフォルトチェックボックス設定時はユーザID未選択、重複チェックは除外
    if ('DEF' == key) {
      if (!_cells[index].USER_ID) {
        console.debug(['none user', _cells[index].USER_ID]);
        return;
      }

      const defIdx = _cells.findIndex((element) => element.DEF != 0);
      console.debug(['defIdx', defIdx]);
      if (defIdx >= 0) {
        _cells[defIdx] = { ..._cells[defIdx], [key]: 0 };
        _cells[defIdx].isEdit = true;
      }
    }

    _cells[index] = { ..._cells[index], [key]: event.target.checked === true ? 1 : 0 };
    _cells[index].isEdit = true;
    setCells(_cells);
    props.updateCells(_cells);
  };

  /**
   * パスワード変更ダイアログ
   */
  const handlePasswordCheckDialogShow = (indexNo: number) => {
    setPassCheck({
      indexNo: indexNo,
      nowPassword: cells[indexNo].PASSWORD,
      newPassword: '',
      confirmationPassword: '',
      hasPassword: cells[indexNo].hasPassword,
    });
    setDialogOpen(true);
  };

  /**
   *パスワード変更処理
   */
  const handlePasswordCheckOkButton = () => {
    let errorMsg = CheckPasswordFormat(passCheck.newPassword, t);
    if (errorMsg !== '') {
      setShowMessage(errorMsg);
      setMessageDialogOpen(true);
      return;
    }
    if (passCheck.newPassword !== passCheck.confirmationPassword) {
      setShowMessage(
        t('LoginUserSettingPage.新しいパスワードと確認用が一致しません', '新しいパスワードと確認用が一致しません')
      );
      setMessageDialogOpen(true);
      return;
    }

    if (passCheck.hasPassword) {
      // 現在のパスワードがあるときのみ
      UpdatePassword(cells[passCheck.indexNo].USER_ID, passCheck.nowPassword, passCheck.newPassword).then((r) => {
        //パスワード変更を反映
        if (r.ErrorCode == 0) {
          setShowMessage(t('LoginUserSettingPage.パスワードが変更されました', 'パスワードが変更されました'));
          setMessageDialogOpen(true);
          setDialogOpen(false);
        } else if (r.ErrorCode == ErrorCode.ERR_CODE_USER_NO_USER) {
          setShowMessage(t('LoginUserSettingPage.選択したユーザID情報が不正です', '選択したユーザID情報が不正です'));
          setMessageDialogOpen(true);
        } else if (r.ErrorCode == ErrorCode.ERR_CODE_USER_PASSWORD_NOMATCH) {
          setShowMessage(t('LoginUserSettingPage.現在のパスワードが違います', '現在のパスワードが違います'));
          setMessageDialogOpen(true);
        } else if (r.ErrorCode == ErrorCode.ERR_CODE_USER_PASSWORD_OLD) {
          setShowMessage(
            t(
              'LoginUserSettingPage.過去に使用したパスワードは使用できません',
              '過去に使用したパスワードは使用できません'
            )
          );
          setMessageDialogOpen(true);
        } else if (r.ErrorCode == ErrorCode.ERR_CODE_SYSTEM_ERROR) {
          setShowMessage(t('LoginUserSettingPage.システムエラーが発生しました', 'システムエラーが発生しました'));
          setMessageDialogOpen(true);
        }
      });
    } else {
      const _cells = [...cells];
      _cells[passCheck.indexNo].PASSWORD = passCheck.newPassword;
      _cells[passCheck.indexNo].isEdit = true;
      setCells(_cells);
      props.updateCells(_cells);
      setDialogOpen(false);
    }
  };
  const getPasswordDisp = (row: USER_MNG): string => {
    var ret = '';

    if (row.hasPassword || row.PASSWORD != '') {
      ret = '●●●●';
    }
    return ret;
  };
  console.debug(['- 画面描画開始 -', cells]);
  return (
    <div>
      <TableContainer component={Paper} className={props.tableContainerStyle}>
        <Table stickyHeader size="small" aria-label="a dense table">
          <TableHead>
            <TableRow>
              <TableCell className={tableBase.headerCell} width={50}>
                {t('LoginUserSettingPage.No', 'No')}
              </TableCell>
              <TableCell className={tableBase.headerCell} width={200}>
                {t('LoginUserSettingPage.ユーザーID', 'ユーザーID')}
              </TableCell>
              <TableCell className={tableBase.headerCell} width={100}>
                {t('LoginUserSettingPage.パスワード', 'パスワード')}
              </TableCell>
              {addressUseenable == true ?
                <TableCell className={tableBase.headerCell} width={200}>
                  {t('LoginUserSettingPage.メールアドレス', 'メールアドレス')}
                </TableCell>
                : null
              }
              <TableCell className={tableBase.headerCell} width={150}>
                {t('LoginUserSettingPage.設定変更', '設定変更')}
              </TableCell>
              <TableCell className={tableBase.headerCell} width={150}>
                {t('LoginUserSettingPage.システム変更', 'システム変更')}
              </TableCell>
              <TableCell className={tableBase.headerCell} width={150}>
                {t('LoginUserSettingPage.ユーザロック', 'ユーザロック')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {cells.map((row, index) => {
              return (
                <TableRow key={index}>
                  {/**No */}
                  <TableCell className={tableBase.bodyCell} component="th" scope="row">
                    {row.ENT_NO}
                  </TableCell>
                  {/**ユーザーID */}
                  <TableCell className={tableBase.bodyCell}>
                    <input
                      className={tableBase.textField}
                      type="text"
                      maxLength={12}
                      defaultValue={row.USER_ID}
                      value={row.USER_ID}
                      onChange={onChangeCell(index, 'USER_ID')}
                    />
                  </TableCell>
                  {/**パスワード */}
                  <TableCell className={tableBase.bodyCell} onClick={() => handlePasswordCheckDialogShow(index)}>
                    <input
                      className={tableBase.textField}
                      type="text"
                      readOnly
                      value={getPasswordDisp(row)}
                      defaultValue={getPasswordDisp(row)}
                    />
                  </TableCell>
                  {/**メールアドレス */}
                  {addressUseenable == true ?
                    <TableCell className={tableBase.bodyCell}>
                      <input
                        className={tableBase.textField}
                        type="text"
                        maxLength={255}
                        defaultValue={row.MAIL_ADDRESS}
                        value={row.MAIL_ADDRESS}
                        onChange={onChangeCell(index, 'MAIL_ADDRESS')}
                      />
                    </TableCell>
                    : null
                  }
                  {/**設定変更 */}
                  <TableCell className={tableBase.bodyCell}>
                    <input
                      type="checkbox"
                      value="js"
                      onChange={onChangeSwitchCell(index, 'SET_AUTH')}
                      checked={row.SET_AUTH === 0 ? false : true}
                    />
                  </TableCell>
                  {/**システム変更 */}
                  <TableCell className={tableBase.bodyCell}>
                    <input
                      type="checkbox"
                      value="js"
                      onChange={onChangeSwitchCell(index, 'SYSTEM_AUTH')}
                      checked={row.SYSTEM_AUTH === 0 || row.SYSTEM_AUTH === null ? false : true}
                    />
                  </TableCell>
                  {/**ユーザロック */}
                  <TableCell className={tableBase.bodyCell}>
                    <input
                      type="checkbox"
                      value="js"
                      onChange={onChangeSwitchCell(index, 'USER_LOCK')}
                      checked={row.USER_LOCK === 0 || row.SYSTEM_AUTH === null ? false : true}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <OKCancelDialog
        open={dialogOpen}
        onOK={() => handlePasswordCheckOkButton()}
        onCancel={() => setDialogOpen(false)}
        title={t('LoginUserSettingGrid.パスワード変更', 'パスワード変更')}
        okText={t('LoginUserSettingGrid.OK', 'OK')}
        cancelText={t('LoginUserSettingGrid.キャンセル', 'キャンセル')}
        maxWidth={'sm'}
      >
        <LoginUserSettingPassWordChangeBody
          updatePassword={updatecheckPassword}
          hasCurrentPass={passCheck.nowPassword !== '' || passCheck.hasPassword}
        />
      </OKCancelDialog>
      <SimpleDialog
        open={messageDialogOpen}
        onClose={() => setMessageDialogOpen(false)}
        title={t('LoginUserSettingGrid.パスワード変更', 'パスワード変更')}
        okText={t('LoginUserSettingGrid.OK', 'OK')}
        maxWidth={'sm'}
      >
        <Typography>{showMessage}</Typography>
      </SimpleDialog>
    </div>
  );
};

export default LoginUserSettingGrid;
